import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Divider } from 'antd';
import { KeyOutlined, ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { images } from '../assets';
import Logo from '../Component/Logo';
import LanguageSwitcher from '../Component/LanguageSwitcher';
import useTwilioWhatsApp from '../Hooks/useTwilioWhatsApp';
import useVerificationCode from '../Hooks/useVerificationCode';
import { VerificationCodeProps } from '../types/types';

const { Title, Text } = Typography;

const VerificationCode: React.FC<VerificationCodeProps> = ({ phoneNumber, onBack, onSuccess }) => {
  const [countdown, setCountdown] = useState(0);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const inputRefs = useRef<(any | null)[]>([]);
  const { resendVerificationCode, loading: resendLoading } = useTwilioWhatsApp();
  const { verifyCode, loading } = useVerificationCode();

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 10000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const onFinish = async (values: { code: string }) => {
    try {
      // Use the verification hook
      const isValid = await verifyCode(values.code, phoneNumber);

      if (isValid) {
        console.log('OTP verification successful, showing reset password form');
        onSuccess();
      }
    } catch (error) {
      console.error('Error verifying code:', error);
    }
  };

  const handleResendCode = async () => {
    try {
      // Use the Twilio hook to resend code
      const verificationCode = await resendVerificationCode(phoneNumber);

      if (verificationCode) {
        setCountdown(60); // 60 seconds countdown
      }
    } catch (error) {
      console.error('Error resending verification code:', error);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    // Update the form value
    const currentCode = form.getFieldValue('code') || '';
    const newCode = currentCode.split('');
    newCode[index] = value;
    const finalCode = newCode.join('');
    form.setFieldsValue({ code: finalCode });

    // Move to next input if value entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit if all 6 digits are entered
    if (finalCode.length === 6) {
      form.submit();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !e.currentTarget.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Format phone number for display (e.g., +966 50 123 4567)
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('966')) {
      const number = cleaned.substring(3);
      return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
    }
    return phone;
  };

  return (
    <div className="responsive-login">
      <div
        className="login-bg-pattern"
        style={{ backgroundImage: `url(${images.bg})` }}
        aria-hidden
      />

      <div className="login-branding">
        <div className="login-branding-inner">
          <Logo size="medium" invert={true} style={{ marginBottom: '30px' }} />
          <Title level={1} className="brand-title">
            {t('branding.DES')}
          </Title>
          <Title level={3} className="brand-subtitle">
            {t('branding.retailManagement')}
          </Title>
          <Text className="brand-tagline">{t('branding.streamlineOperations')}</Text>
        </div>
      </div>

      <div className="login-form">
        <Card>
          <div className="login-form-header">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="login-form-back-btn"
            />
            <KeyOutlined className="login-form-icon" />
            <Title level={2}>{t('auth.verificationTitle')}</Title>
            <Text type="secondary">{t('auth.verificationDescription')}</Text>
            <Text className="login-form-highlight">{formatPhoneNumber(phoneNumber)}</Text>
          </div>

          <Form
            form={form}
            name="verification"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="code"
              rules={[
                { required: true, message: t('validation.required') },
                {
                  validator: (_, value) => {
                    if (!value || value.length !== 6) {
                      return Promise.reject(new Error(t('validation.invalidVerificationCode')));
                    }
                    if (!/^[0-9]{6}$/.test(value)) {
                      return Promise.reject(new Error(t('validation.invalidVerificationCode')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input type="hidden" style={{ display: 'none' }} />
              <div className="login-verification-inputs">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    maxLength={1}
                    className="login-verification-digit"
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
            </Form.Item>

            <Form.Item style={{ marginBottom: '24px' }}>
              <Button type="primary" htmlType="submit" loading={loading} block>
                {loading ? t('auth.verifying') : t('auth.verifyCode')}
              </Button>
            </Form.Item>

            <Divider style={{ margin: '24px 0' }}>
              <Text type="secondary">{t('auth.didntReceive')}</Text>
            </Divider>

            <div style={{ textAlign: 'center' }}>
              <Button
                type="link"
                icon={<ReloadOutlined />}
                loading={resendLoading}
                disabled={countdown > 0}
                onClick={handleResendCode}
              >
                {countdown > 0 ? t('auth.resendIn', { seconds: countdown }) : t('auth.resendCode')}
              </Button>
            </div>

            <div className="login-secure-note" style={{ marginTop: '16px' }}>
              <Text type="secondary">🔐 {t('auth.verificationInstructions')}</Text>
            </div>
          </Form>
        </Card>
      </div>

      <div className="login-language-switcher">
        <LanguageSwitcher type="button" />
      </div>
    </div>
  );
};

export default VerificationCode;
